import { QueryKeys } from "@/constants/Query";
import { AdherenceStatus } from "@/features/Patient/MedicalRecords/MedicationsAllergies/network/useMedications";
import { useApiMutation } from "@/hooks/useApiMutation";
import axiosInstance from "@/lib/axios";
import { wildcardInvalidateQuery } from "@/lib/utils";
import { useQueryClient } from "@tanstack/react-query";

interface PharmacyRoutePrescriptionParams {
  medicationRequestIds: string[];
}

export enum PharmacyRouteError {
  FAILED_TO_SYNC = "FAILED_TO_SYNC",
  FAILED_TO_ROUTE = "FAILED_TO_ROUTE",
  MEDICATION_REQUEST_ALREADY_ROUTED = "MEDICATION_REQUEST_ALREADY_ROUTED",
  MEDICATION_REQUEST_NOT_FOUND = "MEDICATION_REQUEST_NOT_FOUND",
  PATIENT_NOT_FOUND = "PATIENT_NOT_FOUND",
  PRESCRIBER_NOT_FOUND = "PRESCRIBER_NOT_FOUND",
  UNKNOWN_ERROR = "UNKNOWN_ERROR",
  MISSING_REQUIRED_DETAILS = "MISSING_REQUIRED_DETAILS",
  PHARMACY_NOT_ACTIVE = "PHARMACY_NOT_ACTIVE",
  UNMITIGATED_DRUG_INTERACTION_ALERT = "UNMITIGATED_DRUG_INTERACTION_ALERT",
}

export enum PharmacyRouteWarning {
  NOTIFICATION_NOT_RESOLVED = "NOTIFICATION_NOT_RESOLVED",
  NOTIFICATION_NOT_SENT = "NOTIFICATION_NOT_SENT",
  RESOURCES_NOT_UPDATED = "RESOURCES_NOT_UPDATED",
}

interface RoutingFailure {
  medicationRequestId: string;
  failureReason: PharmacyRouteError;
}

interface RoutingWarning {
  medicationRequestId: string;
  warnings: PharmacyRouteWarning[];
}

interface PharmacyRoutePrescriptionResponse {
  bundle: object;
  failures: RoutingFailure[];
  successfulIds: string[];
  warnings: RoutingWarning[];
}

export const usePharmacyRoutePrescription = ({
  patientId,
  encounterId,
  medicationRequestId,
}: {
  patientId: string;
  encounterId: string;
  medicationRequestId: string;
}) => {
  const queryClient = useQueryClient();

  return useApiMutation<
    { data: PharmacyRoutePrescriptionResponse },
    PharmacyRoutePrescriptionParams
  >({
    mutationOptions: {
      mutationKey: QueryKeys.Patient.Chart.Prescriptions.ROUTE_PHARMACY(
        patientId,
        encounterId,
        medicationRequestId,
      ),
      mutationFn: (params) => {
        return axiosInstance.post(
          "/rx/prescriptions/route-to-pharmacy",
          params,
        );
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.Chart.Prescriptions.DEFAULT(
            patientId,
            encounterId,
          ),
        });
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.Medications.ADHERENCE_SECTION(
              AdherenceStatus.ACTIVE,
              patientId,
            ),
        });
        wildcardInvalidateQuery(
          queryClient,
          QueryKeys.Distribution.AVAILABLE_ACTIONS({
            patientId,
            encounterId: "*",
          }),
        );
      },
    },
  });
};

export const usePharmacyRouteAllPrescriptions = ({
  patientId,
}: {
  patientId: string;
}) => {
  const queryClient = useQueryClient();

  return useApiMutation<
    { data: PharmacyRoutePrescriptionResponse },
    PharmacyRoutePrescriptionParams
  >({
    mutationOptions: {
      mutationKey:
        QueryKeys.Patient.Chart.Prescriptions.ROUTE_PHARMACY_ALL(patientId),
      mutationFn: (params) => {
        return axiosInstance.post(
          "/rx/prescriptions/route-to-pharmacy",
          params,
        );
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.Chart.Prescriptions.DEFAULT_PATIENT(patientId),
        });
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.Medications.ADHERENCE_SECTION(
              AdherenceStatus.ACTIVE,
              patientId,
            ),
        });
        wildcardInvalidateQuery(
          queryClient,
          QueryKeys.Distribution.AVAILABLE_ACTIONS({
            patientId,
            encounterId: "*",
          }),
        );
      },
    },
  });
};
